import { cva } from 'class-variance-authority'
import React, { HTMLAttributes, useCallback, useEffect, useState } from 'react'

import { Button } from '@frame/ui-primitives/button'
import { H4 } from '@frame/ui-primitives/h4'
import { H5 } from '@frame/ui-primitives/h5'
import { Span } from '@frame/ui-primitives/span'

import checkmark from '@frame/images/checkmark.svg'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { getVerifierDisplayName } from '@frame/lib/utils/session'
import { DataAttribute } from '@frame/types'
import { Transition } from '@headlessui/react'

import { AnimatedCheckmark } from './animated-checkmark'
import { HoverOverlay } from './hover-overlay'
import { TaskVisualStatus } from './types'
import {
  getButtonIcon,
  getStatusCanRetry,
  getStatusIsHoverable,
  getTextFromVisualStatus,
  isNotAttempted,
  isStatusFailure,
  isStatusSuccessful,
} from './utils'

const cardVariants = cva(
  'py-2 px-4 relative rounded-md bg-white min-h-[76px] z-30 flex items-center justify-between border-2 border-moss-200',
  {
    variants: {
      isNotAttempted: {
        true: 'border-moss-300',
      },
      isHoverable: {
        true: 'hover:cursor-pointer',
      },
      isLoading: {
        true: 'pointer-events-none opacity-75',
      },
      showBorderAnimation: {
        true: 'border-none gradient-border overflow-hidden',
      },
    },
    defaultVariants: {
      isNotAttempted: true,
      isHoverable: false,
      isLoading: false,
    },
  }
)

const textVariants = cva('text-cobranding font-medium', {
  variants: {
    isSuccess: {
      true: 'text-mint-600 font-medium',
    },
    isFailure: {
      true: 'text-moss-500 font-medium',
    },
  },
  defaultVariants: {
    isSuccess: false,
    isFailure: false,
  },
})

const buttonVariants = cva(
  'h-6 w-6 rounded-full relative z-30 group-hover:shadow-floatingButton group-hover:-translate-y-[2px]',
  {
    variants: {
      isCobranded: {
        true: 'bg-cobranding border-cobranding hover:bg-cobranding',
        false: 'bg-moss-200 border-none text-moss-700 hover:bg-moss-200',
      },
    },
    defaultVariants: {
      isCobranded: true,
    },
  }
)

type Props = {
  title: string
  status: TaskVisualStatus
  isNext?: boolean
  isLoading?: boolean
  onClick?: () => void
  className?: HTMLAttributes<HTMLDivElement>['className']
  animate?: boolean
}

export function DirectTaskCard({
  title,
  status,
  isLoading = false,
  animate = false,
  isNext = false,
  onClick,
  className,
}: Props) {
  const { verifierName, verifierType, tenantPropertyName } = useStore(({ session }) => ({
    verifierName: session?.originating_party_display_name,
    tenantPropertyName: session?.configuration?.cobranding?.tenant_property?.property_name,
    verifierType: session?.verifier_type,
  }))
  const [isBackgroundVisible, setIsBackgroundVisible] = useState(false)
  const [animateCheckmark, setAnimateCheckmark] = useState(false)
  const isHoverable = getStatusIsHoverable(status)
  const canRetry = getStatusCanRetry(status)
  const hasOverlay = isHoverable && !canRetry
  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)
  const detail = getTextFromVisualStatus(status, displayName)
  const btnIcon = getButtonIcon(status, isLoading)
  const isSuccess = isStatusSuccessful(status)
  const isFailure = isStatusFailure(status)
  const showBorderAnimation = isNext && isNotAttempted(status)

  useEffect(() => {
    const delayTimeout = setTimeout(() => {
      setIsBackgroundVisible(true)
    }, 100)

    const timeout = setTimeout(() => {
      setIsBackgroundVisible(false)
    }, 750)

    return () => {
      clearTimeout(delayTimeout)
      clearTimeout(timeout)
    }
  }, [])

  const handleClick = useCallback(() => {
    if (isHoverable && onClick) {
      onClick()
    }
  }, [isHoverable, onClick])

  if (animate) {
    return (
      <div
        data-cy={DataAttribute.EMPLOYMENT_CARD}
        className={cn('group relative', isLoading || (!onClick && 'pointer-events-none'))}
        onClick={handleClick}
        role="button"
      >
        <div className={cn(cardVariants({ isHoverable: false, isLoading }), className)}>
          <Transition
            show={isBackgroundVisible}
            as="div"
            enter="transition-opacity duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100 border-moss-200"
            leave="transition-opacity duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            beforeLeave={() => setAnimateCheckmark(true)}
            className="absolute inset-0 -left-[2px] -top-[2px] h-[calc(100%+4px)] w-[calc(100%+4px)] rounded-md border-2 border-mint-600 bg-mint-200"
          />
          <div className="z-20">
            <H4 className="text-moss-700" as="div">
              {title}
            </H4>
            <Transition
              show
              appear
              as="div"
              enter="transition-opacity duration-500 delay-100"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <H5 className={cn(textVariants({ isSuccess: true }))} as="div">
                {detail}
              </H5>
            </Transition>
          </div>
          <div className="flex items-center">{<AnimatedCheckmark show={animateCheckmark} />}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-cy={DataAttribute.EMPLOYMENT_CARD}
      className={cn('group relative', isLoading && 'pointer-events-none')}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      role="button"
    >
      <div
        tabIndex={0}
        className={cn(
          cardVariants({ isHoverable, isLoading, showBorderAnimation, isNotAttempted: isNotAttempted(status) }),
          className
        )}
      >
        {hasOverlay && <HoverOverlay isNext={isNext} />}
        <div className="z-20">
          <H4 className="text-moss-700" as="div">
            {title}
          </H4>
          <H5 className={cn(textVariants({ isSuccess, isFailure }))} as="div">
            {detail}
          </H5>
        </div>
        <div className="flex items-center">
          {canRetry && (
            <Span className="mr-2 font-medium transition-transform group-hover:-translate-y-[2px]">Retry</Span>
          )}
          {isHoverable ? (
            <Button
              tabIndex={-1}
              Icon={btnIcon}
              className={cn(buttonVariants({ isCobranded: !canRetry }))}
              aria-label="Retry"
            />
          ) : (
            <img src={checkmark} className="min-w-[32px]" alt="Status" />
          )}
        </div>
      </div>
    </div>
  )
}
